import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Compass } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { TourEngine, type TourEndReason } from "@/components/onboarding/TourEngine";
import { TourTooltip } from "@/components/onboarding/TourTooltip";
import type { TourStep } from "@/components/onboarding/tour-steps";
import { usePageTourRegistrar, usePageTourContext } from "@/contexts/PageTourContext";

/**
 * Per-page onboarding tour — custom engine (no react-joyride).
 *
 * Each page owns its own tour via this hook. Because this is a single-page app,
 * only one page is mounted at a time, so a single engine instance per page
 * never conflicts with another.
 *
 * Behavior:
 * - Auto-starts on page mount for authenticated users who haven't dismissed
 *   the tour via Close/Skip/Finish. Once dismissed (via close or completion),
 *   the tour won't auto-start again until the user manually replays it.
 * - Exposes `startTour()` to replay the page tour on demand (clears the
 *   completed/snoozed flags, restarts from step 0). A `<TourHelpButton>` is
 *   provided for this.
 * - Three dismissal modes:
 *   - Close (X): permanent dismiss → writes `tour-completed:<routeKey>`
 *   - Skip (same as close on the last step's "Got it")
 *   - Snooze ("Remind me later"): session-only → writes `tour-snoozed:<routeKey>`
 *     to sessionStorage. Auto-start skips if this key exists in the current session.
 * - Global suppression: if `tour-globally-suppressed` is set in localStorage,
 *   no tour auto-starts (used by E2E `disableTour`).
 * - Auth gating: auto-start only fires for authenticated users. `undefined`
 *   (still loading) defers the decision; `false` never starts.
 *
 * E2E contract: `disableTour` sets `tour-globally-suppressed` to suppress ALL
 * page tours at once. It also sets the legacy `tour-completed` and
 * `onboarding-completed` keys for backward compatibility. The Home page uses
 * routeKey "home" (gated by the bare `tour-completed` key) so existing specs
 * that only set that key still suppress Home's auto-start unchanged.
 */

interface UsePageTourOptions {
  routeKey: string;
  steps: TourStep[];
  disableAutoStart?: boolean;
  /**
   * Register this tour's `startTour` with the PageTourContext so the global
   * "Take a tour" entry can trigger it. Defaults to true. Set false for a
   * secondary tour on a page that already has a primary one (otherwise the
   * second instance clobbers the first's registered starter).
   */
  register?: boolean;
}

interface UsePageTourResult {
  joyride: ReactElement | null;
  startTour: () => void;
  stepIndex: number;
}

const COMPLETED_PREFIX = "tour-completed";
const SNOOZE_PREFIX = "tour-snoozed";
const GLOBAL_SUPPRESS_KEY = "tour-globally-suppressed";

const SUPPRESSED_ROUTE_PATTERNS = [
  /^\/auth(\/|$)/,
  /^\/session\/.+\/practice$/,
  /^\/call\//,
];

const isSuppressedRoute = (pathname: string): boolean =>
  SUPPRESSED_ROUTE_PATTERNS.some((re) => re.test(pathname));

const storageKeyFor = (routeKey: string): string =>
  routeKey === "home" ? "tour-completed" : `${COMPLETED_PREFIX}:${routeKey}`;

const snoozeKeyFor = (routeKey: string): string =>
  routeKey === "home" ? "tour-snoozed" : `${SNOOZE_PREFIX}:${routeKey}`;

const writeCompleted = (routeKey: string): void => {
  try {
    localStorage.setItem(storageKeyFor(routeKey), "true");
    if (routeKey === "home") {
      localStorage.setItem("onboarding-completed", "true");
    }
  } catch {
    // ignore
  }
};

const readCompleted = (routeKey: string): boolean => {
  try {
    return localStorage.getItem(storageKeyFor(routeKey)) === "true";
  } catch {
    return false;
  }
};

const clearCompleted = (routeKey: string): void => {
  try {
    localStorage.removeItem(storageKeyFor(routeKey));
    if (routeKey === "home") {
      localStorage.removeItem("onboarding-completed");
    }
  } catch {
    // ignore
  }
};

const isGloballySuppressed = (): boolean => {
  try {
    return localStorage.getItem(GLOBAL_SUPPRESS_KEY) === "true";
  } catch {
    return false;
  }
};

const isSnoozed = (routeKey: string): boolean => {
  try {
    return sessionStorage.getItem(snoozeKeyFor(routeKey)) === "true";
  } catch {
    return false;
  }
};

const writeSnoozed = (routeKey: string): void => {
  try {
    sessionStorage.setItem(snoozeKeyFor(routeKey), "true");
  } catch {
    // ignore
  }
};

const clearSnoozed = (routeKey: string): void => {
  try {
    sessionStorage.removeItem(snoozeKeyFor(routeKey));
  } catch {
    // ignore
  }
};

const targetIsReady = (steps: TourStep[]): boolean =>
  steps.every(
    (s) =>
      s.target === "body" ||
      s.hideIfMissing ||
      (typeof s.target === "string" && document.querySelector(s.target) != null)
  );

/**
 * Wait for all step targets to be ready using MutationObserver.
 * Falls back to a 5s timeout. More reliable than fixed polling.
 */
const waitForTargets = (steps: TourStep[], onReady: () => void): void => {
  if (targetIsReady(steps)) {
    onReady();
    return;
  }

  let settled = false;
  const settle = (fn: () => void) => {
    if (settled) return;
    settled = true;
    observer?.disconnect();
    clearTimeout(timer);
    fn();
  };

  const observer = new MutationObserver(() => {
    if (targetIsReady(steps)) {
      settle(onReady);
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  const timer = setTimeout(() => settle(onReady), 5000);
};

export const usePageTour = ({ routeKey, steps: rawSteps, disableAutoStart = false, register = true }: UsePageTourOptions): UsePageTourResult => {
  const location = useLocation();
  const { data: isAuthenticated } = useAuth();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [steps, setSteps] = useState<TourStep[]>(rawSteps);
  const autoStartedRef = useRef(false);
  const registerStarter = usePageTourRegistrar();
  const { setTourActive } = usePageTourContext();

  // Sync tour active state with the context so other overlays can defer.
  useEffect(() => {
    setTourActive(run);
    return () => setTourActive(false);
  }, [run, setTourActive]);

  const stop = useCallback(() => {
    setRun(false);
    setTourActive(false);
    writeCompleted(routeKey);
  }, [routeKey, setTourActive]);

  const handleEnd = useCallback(
    (reason: TourEndReason) => {
      if (reason === "snoozed") {
        writeSnoozed(routeKey);
        setRun(false);
        setTourActive(false);
      } else {
        // finished, skipped, closed → permanent dismiss
        setRun(false);
        setTourActive(false);
        writeCompleted(routeKey);
      }
    },
    [routeKey, setTourActive]
  );

  const startTour = useCallback(() => {
    clearCompleted(routeKey);
    clearSnoozed(routeKey);
    setStepIndex(0);
    // Pass full steps to waitForTargets — targetIsReady skips hideIfMissing
    // steps regardless of target existence. After targets are confirmed, we
    // filter out hideIfMissing steps whose targets are still absent.
    setSteps(rawSteps);
    // Set tour active IMMEDIATELY (before async target resolution) so that
    // other overlays like the ReviewReminder defer showing while we wait.
    setTourActive(true);
    requestAnimationFrame(() => {
      waitForTargets(rawSteps, () => {
        // DOM is now ready — safely filter hideIfMissing steps.
        const filtered = rawSteps.filter(
          (s) => !(s.hideIfMissing && s.target !== "body" && !document.querySelector(s.target))
        );
        setSteps(filtered);
        setRun(true);
      });
    });
  }, [routeKey, rawSteps, setTourActive]);

  // Auto-start on every mount for authenticated users, ignoring prior
  // completion. `undefined` (auth still loading) defers so an authenticated
  // user isn't locked out before the session resolves.
  useEffect(() => {
    if (isAuthenticated === undefined) return;
    if (autoStartedRef.current) return;
    autoStartedRef.current = true;
    if (disableAutoStart) return;
    if (isSuppressedRoute(location.pathname)) return;
    if (isGloballySuppressed()) return;
    if (isSnoozed(routeKey)) return;
    if (readCompleted(routeKey)) return;
    if (isAuthenticated !== true) return;
    startTour();
  }, [disableAutoStart, routeKey, location.pathname, startTour, isAuthenticated]);

  // Register this page's starter so the global "Take a tour" entry (AvatarMenu)
  // and any out-of-tree caller can trigger the current page's tour.
  useEffect(() => {
    if (!register) return;
    if (!registerStarter) return;
    registerStarter(startTour);
    return () => {
      registerStarter(null);
    };
  }, [register, registerStarter, startTour]);

  const handleNext = useCallback(() => {
    if (stepIndex >= steps.length - 1) {
      handleEnd("finished");
    } else {
      setStepIndex((i) => i + 1);
    }
  }, [stepIndex, steps.length, handleEnd]);

  const handleBack = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const handleClose = useCallback(() => {
    handleEnd("closed");
  }, [handleEnd]);

  const handleSnooze = useCallback(() => {
    handleEnd("snoozed");
  }, [handleEnd]);

  const handleJump = useCallback(
    (index: number) => {
      if (index < 0 || index >= steps.length) return;
      setStepIndex(index);
    },
    [steps.length]
  );

  const joyride = useMemo<ReactElement | null>(() => {
    if (!run || isSuppressedRoute(location.pathname) || steps.length === 0) return null;
    return (
      <TourEngine
        steps={steps}
        stepIndex={stepIndex}
        renderTooltip={TourTooltip}
        onNext={handleNext}
        onBack={handleBack}
        onClose={handleClose}
        onSnooze={handleSnooze}
        onJump={handleJump}
      />
    );
  }, [run, location.pathname, steps, stepIndex, handleNext, handleBack, handleClose, handleSnooze, handleJump]);

  return { joyride, startTour, stepIndex };
};

interface TourHelpButtonProps {
  className?: string;
  label?: string;
}

/**
 * A small outline button that triggers the CURRENT page's tour (resolved via
 * the PageTourContext registrar). Drop it into a PageHero `actions` slot or a
 * page header — it needs no props. Uses the `Compass` icon to match the
 * existing AvatarMenu "Take a tour" entry.
 */
export const TourHelpButton = ({ className, label }: TourHelpButtonProps) => {
  const { t } = useTranslation();
  const { startCurrentPageTour } = usePageTourContext();
  return (
    <button
      type="button"
      onClick={startCurrentPageTour}
      className={cn(
        "inline-flex h-9 items-center justify-center gap-1.5 rounded-full border border-input bg-background px-4 text-xs font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground",
        className
      )}
    >
      <Compass className="h-3.5 w-3.5" />
      {label ?? t("pages.onboarding.help.showMeAround", "Show me around")}
    </button>
  );
};

export default usePageTour;